import './BrandingLocation.scss';
import React from 'react';
import clsx from 'clsx';
import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import ArrButton, {
  ArrButtonVariant,
} from '../../../../common/arr-button/ArrButton';
import {Add as AddIcon} from '@mui/icons-material';
import {BrandingPositionKey} from '../../../../../api/types/production.type';

interface BrandingLocationProps {
  currentBrandingPosition: BrandingPositionKey;
  expandedPlusButtons: {[key: string]: boolean};
  onBrandingPositionChange: (position: BrandingPositionKey) => void;
  onPlusButtonClick: (key: string) => void;
  onReset: () => void;
  isResetEnabled: boolean;
  getBrandingPositionButtonVariant: (
    position: BrandingPositionKey
  ) => ArrButtonVariant;
  getBrandingPositionButtonClassName: (position: BrandingPositionKey) => string;
  getPositionDisplayText: (position: BrandingPositionKey) => string;
  disabled?: boolean;
}

const BrandingLocation: React.FC<BrandingLocationProps> = ({
  currentBrandingPosition,
  expandedPlusButtons,
  onBrandingPositionChange,
  onPlusButtonClick,
  onReset,
  isResetEnabled,
  getBrandingPositionButtonVariant,
  getBrandingPositionButtonClassName,
  getPositionDisplayText,
  disabled = false,
}) => {
  const {t} = useTranslation();

  const getLocationButtonA11yProps = (
    position: BrandingPositionKey,
    label: string
  ) => ({
    'aria-label': `${label} branding location`,
    'aria-pressed': currentBrandingPosition === position,
    'aria-disabled': disabled,
    tabIndex: disabled ? -1 : 0,
  });

  const getPlusButtonA11yProps = (label: string) => ({
    'aria-label': label,
    'aria-disabled': disabled,
    tabIndex: disabled ? -1 : 0,
  }); // TIFF/PDF A11Y CHANGE: plus buttons have descriptive labels and are removed from tab order in read-only mode.

  const getResetButtonA11yProps = () => ({
    'aria-label': 'Reset branding location',
    'aria-disabled': disabled || !isResetEnabled,
    tabIndex: disabled || !isResetEnabled ? -1 : 0,
  });

  const handleLocationKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    position: BrandingPositionKey
  ) => {
    if (disabled) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onBrandingPositionChange(position);
    }
  };

  const handlePlusKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    key: string
  ) => {
    if (disabled) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onPlusButtonClick(key);
    }
  };

  const renderBrandingRow = (type: 'header' | 'footer') => {
    const leftCenterKey = `${type}-left-center`;
    const centerRightKey = `${type}-center-right`;

    // Map type to proper BrandingPositionKey format
    const positionMap = {
      header: {
        left: 'leftHeaderBranding' as BrandingPositionKey,
        right: 'rightHeaderBranding' as BrandingPositionKey,
        center: 'centerHeaderBranding' as BrandingPositionKey,
        leftCenter: 'leftCenterHeaderBranding' as BrandingPositionKey,
        rightCenter: 'rightCenterHeaderBranding' as BrandingPositionKey,
        full: 'headerBranding' as BrandingPositionKey,
      },
      footer: {
        left: 'leftFooterBranding' as BrandingPositionKey,
        right: 'rightFooterBranding' as BrandingPositionKey,
        center: 'centerFooterBranding' as BrandingPositionKey,
        leftCenter: 'leftCenterFooterBranding' as BrandingPositionKey,
        rightCenter: 'rightCenterFooterBranding' as BrandingPositionKey,
        full: 'footerBranding' as BrandingPositionKey,
      },
    };

    const leftPosition = positionMap[type].left;
    const rightPosition = positionMap[type].right;
    const centerPosition = positionMap[type].center;
    const leftCenterPosition = positionMap[type].leftCenter;
    const rightCenterPosition = positionMap[type].rightCenter;
    const fullPosition = positionMap[type].full;
    const rowLabel = type === 'header' ? 'Top row' : 'Bottom row';

    return (
      <Box
        className="branding-row"
        role="group"
        aria-label={`${rowLabel} branding location selector`}
      >
        {expandedPlusButtons[leftCenterKey] &&
        expandedPlusButtons[centerRightKey] ? (
          <ArrButton
            dataTestId={`${type}-full-expanded-btn`}
            variant={getBrandingPositionButtonVariant(fullPosition)}
            size="small"
            onClick={() => !disabled && onBrandingPositionChange(fullPosition)}
            onKeyDown={(event) => handleLocationKeyDown(event, fullPosition)}
            className={`${getBrandingPositionButtonClassName(fullPosition)} btn-full-expanded typo_text buttonSmall_size`}
            disabled={disabled}
            {...getLocationButtonA11yProps(
              fullPosition,
              `${rowLabel} expanded full`
            )}
          >
            Expanded
          </ArrButton>
        ) : (
          <>
            {/* Left Button or Left-Center Button */}
            {expandedPlusButtons[leftCenterKey] ? (
              <ArrButton
                dataTestId={`${type}-left-center-expanded-btn`}
                variant={getBrandingPositionButtonVariant(leftCenterPosition)}
                size="small"
                onClick={() =>
                  !disabled && onBrandingPositionChange(leftCenterPosition)
                }
                onKeyDown={(event) =>
                  handleLocationKeyDown(event, leftCenterPosition)
                }
                className={`${getBrandingPositionButtonClassName(leftCenterPosition)} btn-half-expanded typo_text buttonSmall_size`}
                disabled={disabled}
                {...getLocationButtonA11yProps(
                  leftCenterPosition,
                  `${rowLabel} left-center expanded`
                )}
              >
                Expanded
              </ArrButton>
            ) : (
              <ArrButton
                dataTestId={`${type}-left-btn`}
                variant={getBrandingPositionButtonVariant(leftPosition)}
                size="small"
                onClick={() =>
                  !disabled && onBrandingPositionChange(leftPosition)
                }
                onKeyDown={(event) =>
                  handleLocationKeyDown(event, leftPosition)
                }
                className={`${getBrandingPositionButtonClassName(leftPosition)} typo_text buttonSmall_size`}
                disabled={disabled}
                {...getLocationButtonA11yProps(
                  leftPosition,
                  `${rowLabel} left`
                )}
              >
                Left
              </ArrButton>
            )}

            {/* Line - only show if left-center plus is not expanded */}
            {!expandedPlusButtons[leftCenterKey] && (
              <Box className="line"></Box>
            )}

            {/* Left-Center Plus Button */}
            {!expandedPlusButtons[leftCenterKey] && (
              <ArrButton
                variant="secondary"
                size="small"
                className="btn-expand"
                dataTestId={`${type}-left-center-plus-btn`}
                onClick={() => !disabled && onPlusButtonClick(leftCenterKey)}
                onKeyDown={(event) => handlePlusKeyDown(event, leftCenterKey)}
                disabled={disabled}
                {...getPlusButtonA11yProps(
                  `${rowLabel} add left-center branding location`
                )}
              >
                <AddIcon />
              </ArrButton>
            )}

            {/* Line - show if left-center is not expanded OR if center-right is expanded */}
            {(!expandedPlusButtons[leftCenterKey] ||
              expandedPlusButtons[centerRightKey]) && (
              <Box className="line"></Box>
            )}

            {/* Center Button - only show when no expansions are active */}
            {!expandedPlusButtons[leftCenterKey] &&
              !expandedPlusButtons[centerRightKey] && (
                <ArrButton
                  variant={getBrandingPositionButtonVariant(centerPosition)}
                  size="small"
                  onClick={() =>
                    !disabled && onBrandingPositionChange(centerPosition)
                  }
                  onKeyDown={(event) =>
                    handleLocationKeyDown(event, centerPosition)
                  }
                  className={`${getBrandingPositionButtonClassName(centerPosition)} typo_text buttonSmall_size`}
                  dataTestId={`${type}-center-btn`}
                  disabled={disabled}
                  {...getLocationButtonA11yProps(
                    centerPosition,
                    `${rowLabel} center`
                  )}
                >
                  Center
                </ArrButton>
              )}

            {/* Right/Center Button - only show when center-right plus is expanded */}
            {expandedPlusButtons[centerRightKey] && (
              <ArrButton
                variant={getBrandingPositionButtonVariant(rightCenterPosition)}
                dataTestId={`${type}-right-center-expanded-btn`}
                size="small"
                onClick={() =>
                  !disabled && onBrandingPositionChange(rightCenterPosition)
                }
                onKeyDown={(event) =>
                  handleLocationKeyDown(event, rightCenterPosition)
                }
                className={`${getBrandingPositionButtonClassName(rightCenterPosition)} btn-half-expanded typo_text buttonSmall_size`}
                disabled={disabled}
                {...getLocationButtonA11yProps(
                  rightCenterPosition,
                  `${rowLabel} right-center expanded`
                )}
              >
                Expanded
              </ArrButton>
            )}

            {/* Line - only show if center-right plus is not expanded */}
            {!expandedPlusButtons[centerRightKey] && (
              <Box className="line"></Box>
            )}

            {/* Center-Right Plus Button */}
            {!expandedPlusButtons[centerRightKey] && (
              <ArrButton
                variant="secondary"
                dataTestId={`${type}-right-btn`}
                size="small"
                className="btn-expand"
                onClick={() => !disabled && onPlusButtonClick(centerRightKey)}
                onKeyDown={(event) => handlePlusKeyDown(event, centerRightKey)}
                disabled={disabled}
                {...getPlusButtonA11yProps(
                  `${rowLabel} add center-right branding location`
                )}
              >
                <AddIcon />
              </ArrButton>
            )}

            {/* Line - only show if center-right plus is not expanded */}
            {!expandedPlusButtons[centerRightKey] && (
              <Box className="line"></Box>
            )}

            {/* Right Button */}
            {!expandedPlusButtons[centerRightKey] && (
              <ArrButton
                dataTestId="center-right"
                variant={getBrandingPositionButtonVariant(rightPosition)}
                size="small"
                onClick={() =>
                  !disabled && onBrandingPositionChange(rightPosition)
                }
                onKeyDown={(event) =>
                  handleLocationKeyDown(event, rightPosition)
                }
                className={`${getBrandingPositionButtonClassName(rightPosition)} typo_text buttonSmall_size`}
                disabled={disabled}
                {...getLocationButtonA11yProps(
                  rightPosition,
                  `${rowLabel} right`
                )}
              >
                Right
              </ArrButton>
            )}
          </>
        )}
      </Box>
    );
  };

  return (
    <Box
      className={clsx(
        'branding-location-section',
        disabled && 'branding-location-section--read-only'
      )}
    >
      <Typography
        className="typo-text subhead4_size branding-location-title"
        data-testid={`branding-location-title`}
      >
        {t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.branding.location.title'
        )}
      </Typography>
      <Box className="branding-location-grid">
        {/* Header Row */}
        {renderBrandingRow('header')}

        {/* Position Display */}
        <Box className="location-display">
          <Box className="location-box">
            <Typography
              className="typo-text label3_size location-label"
              data-testid={`branding-location-location-label`}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.branding.location.locationLabel'
              )}
            </Typography>
            <Typography
              className="typo-text subhead1_size location-text"
              data-testid={`branding-location-position-text`}
              aria-live="polite"
            >
              {getPositionDisplayText(currentBrandingPosition)}
            </Typography>
            <ArrButton
              variant="secondary"
              size="small"
              className="reset-btn typo_text buttonSmall_size"
              onClick={() => !disabled && isResetEnabled && onReset()}
              disabled={disabled || !isResetEnabled}
              dataTestId="branding-location-reset-button"
              {...getResetButtonA11yProps()}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.branding.location.reset'
              )}
            </ArrButton>
          </Box>
        </Box>

        {/* Footer Row */}
        {renderBrandingRow('footer')}
      </Box>
    </Box>
  );
};

export default BrandingLocation;
